import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { COMPANY } from '@/config/site';

const RefundsAndCancellation = () => {
  const lastUpdated = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="refunds-and-cancellation"
        defaultTitle="Refunds & Cancellation Policy | House Plan Direct"
        defaultDescription="House Plan Direct refunds and cancellation policy for the Managed Redraw Service and the Plan Measure Tool. UK consumer-rights compliant."
      />
      <Header />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <h1
          className="mb-4 text-4xl font-bold text-gray-900"
          data-testid="refunds-title"
        >
          Refunds &amp; Cancellation Policy
        </h1>
        <p className="mb-8 text-sm text-gray-500">
          <strong>Last Updated:</strong> {lastUpdated}
        </p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700">
          <section>
            <p>
              This policy sets out how refunds and cancellations work across
              our two services. Your statutory rights under the{' '}
              <strong>Consumer Rights Act 2015</strong> and{' '}
              <strong>Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013</strong>{' '}
              are not affected. For the full contractual terms, see our{' '}
              <a
                href="/legal/terms"
                className="text-blue-600 hover:underline"
              >
                Terms &amp; Conditions
              </a>
              .
            </p>
          </section>

          <section data-testid="refunds-managed-service">
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              1. Managed Redraw Service (professional CAD floor plans)
            </h2>
            <p>
              This covers orders placed through <code>/order</code> where our
              team produces a CAD floor plan, elevation or section from your
              sketch, photos or measurements.
            </p>

            <h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
              1.1 Before work begins
            </h3>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                You may cancel for a <strong>full refund</strong> at any point
                before we allocate a draughtsperson and start work on your
                plan.
              </li>
              <li>
                Cancel by emailing{' '}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-blue-600 hover:underline"
                >
                  {COMPANY.email}
                </a>{' '}
                quoting your order number.
              </li>
              <li>
                Refunds are processed to the original PayPal payment method
                within <strong>5–7 working days</strong>.
              </li>
            </ul>

            <h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
              1.2 After work has begun (partial refund)
            </h3>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                If drafting has started, we will refund the balance of your
                order minus a fair charge for the work already completed.
              </li>
              <li>
                We will send you a short breakdown of hours completed and any
                external costs before processing the refund.
              </li>
            </ul>

            <h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
              1.3 After delivery of the final files
            </h3>
            <ul className="ml-6 list-disc space-y-2">
              <li>
                We do not offer refunds once the final PDF, JPEG and DWG files
                have been delivered and accepted, because the digital service
                has been fully performed.
              </li>
              <li>
                If you believe the plan contains a{' '}
                <strong>conversion error that is our fault</strong> (for
                example we misread a room label or lost a dimension you gave
                us), tell us within <strong>7 days</strong> of delivery and we
                will correct it free of charge.
              </li>
              <li>
                Where we are unable to correct a proven conversion error, you
                may be entitled to a partial or full refund at our reasonable
                discretion, in line with your statutory rights.
              </li>
            </ul>

            <h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
              1.4 Digital-content statutory notice
            </h3>
            <p>
              Because our Managed Redraw is a digital service performed to
              your specifications and delivered by email, you accept that the
              standard 14-day &ldquo;change-of-mind&rdquo; cancellation right
              under the Consumer Contracts Regulations{' '}
              <strong>ends as soon as delivery begins</strong> with your
              agreement. This does not affect your right to a refund for
              faulty work.
            </p>
          </section>

          <section data-testid="refunds-plan-measure">
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              2. Plan Measure Tool (self-service, free during beta)
            </h2>
            <p>
              This covers our browser-based Plan Measure tool at{' '}
              <a
                href="/measurement-tool"
                className="text-blue-600 hover:underline"
              >
                /measurement-tool
              </a>
              . The tool is currently <strong>free to use during our public
              beta</strong>. Your uploaded plans stay in your browser — we do
              not process them on our servers. As no payment is taken, there is
              nothing to refund. If we reintroduce a paid tier in future,
              detailed refund terms will apply at that time and will be posted
              on this page before any charges are made.
            </p>
          </section>

          <section data-testid="refunds-cancellation-by-us">
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              3. Cancellation by us
            </h2>
            <p>
              We may cancel an order or revoke access and refund you in full
              if:
            </p>
            <ul className="ml-6 list-disc space-y-2">
              <li>we cannot verify or process your payment;</li>
              <li>
                the uploaded material appears to breach our Terms or the
                copyright warranties in Section 7A of our Terms;
              </li>
              <li>
                a genuine pricing error was displayed and the correct price
                would have significantly changed your decision.
              </li>
            </ul>
          </section>

          <section data-testid="refunds-chargebacks">
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              4. Chargebacks and disputes
            </h2>
            <p>
              Please contact us before raising a PayPal dispute or card
              chargeback — the vast majority of issues are resolved by email
              within a working day. If you open a chargeback while we are
              actively delivering your order, we may pause the work until the
              dispute is resolved.
            </p>
          </section>

          <section data-testid="refunds-complaints-link">
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              5. Complaints
            </h2>
            <p>
              If you are unhappy with the outcome of a refund request, our
              full complaints procedure is available at{' '}
              <a
                href="/legal/complaints"
                className="text-blue-600 hover:underline"
              >
                /legal/complaints
              </a>
              . You may also seek independent advice from{' '}
              <a
                href="https://www.citizensadvice.org.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Citizens Advice
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mt-8 mb-4 text-2xl font-bold text-gray-900">
              6. Contact
            </h2>
            <div
              className="rounded-lg border border-blue-200 bg-blue-50 p-6"
              data-testid="refunds-contact-block"
            >
              <p>
                <strong>{COMPANY.tradingAs}</strong>
              </p>
              <p>
                Email:{' '}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-blue-600 hover:underline"
                >
                  {COMPANY.email}
                </a>
              </p>
              <p>Telephone: {COMPANY.telephone}</p>
              <p>Postal Address: {COMPANY.tradingAddress}</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundsAndCancellation;
